/**
 * Typed models for the Common Balance Pool (CBP) feature — MDA 1.1 Phase 2.
 *
 * These mirror the Coral API contract in `ICTransferPricingWebAPILambdaModel`
 * (CR-297689232, `model/resources/common-balance-pool/`) so the frontend codes
 * against the real request/response shapes. `CommonBalancePoolService` calls the
 * live CBP WebAPI through `ApiCallService` — there is no mock layer.
 */

// The shared lifecycle status is reused across MDA sheets/worksheets and CBP.
import { WorksheetStatus } from 'src/models/multidimensional-allocation/MultiAllocationSheetData';

export type CommonBalancePoolStatus = WorksheetStatus;

/**
 * The grain a pool is keyed on — one COA segment dimension per pool.
 * `ACCOUNT` and `LG_CODE` are deliberately absent (not segment dimensions).
 */
export enum CommonBalancePoolGrain {
    COST_CENTER = 'COST_CENTER',
    LEGAL_ENTITY = 'LEGAL_ENTITY',
    LOCATION = 'LOCATION',
    PRODUCT_LINE = 'PRODUCT_LINE',
    CHANNEL = 'CHANNEL',
    PROJECT = 'PROJECT',
}

/** Whether a column is sourced from data (INPUT) or computed from other columns (CALC). */
export type CommonBalancePoolColumnKind = 'INPUT' | 'CALC';

/** How a value renders. Storage is always numeric; PERCENTAGE multiplies by 100 on render only. */
export type CommonBalancePoolColumnDataType = 'NUMBER' | 'PERCENTAGE' | 'CURRENCY';

/** Where an INPUT column reads its values from. */
export type CommonBalancePoolColumnSourceType = 'SEGMENT_CDT' | 'MDA_OUTPUT';

/**
 * Physical source pointer for an INPUT column. For SEGMENT_CDT both the table and the value ID
 * within it are needed — a segment CDT keys its values by value ID (e.g. "value1"), not the
 * display label.
 */
export interface CommonBalancePoolColumnSourceRef {
    cdtTableId?: string;
    /** Human-readable CDT table name, for display in the builder (the id is the stable reference). */
    cdtTableName?: string;
    cdtColumnId?: string;
    mdaSheetId?: string;
    /** Human-readable MDA sheet name, for display in the builder. */
    mdaSheetName?: string;
    mdaSheetVersion?: number;
}

/** One column of a pool — its identity, how it is sourced or computed, and how it renders. */
export interface CommonBalancePoolColumn {
    columnId: string;
    displayName: string;
    kind: CommonBalancePoolColumnKind;
    dataType?: CommonBalancePoolColumnDataType;
    /** Zero-based render order in the builder and preview grid. */
    columnOrder?: number;
    // INPUT only.
    sourceType?: CommonBalancePoolColumnSourceType;
    sourceRef?: CommonBalancePoolColumnSourceRef;
    /**
     * INPUT only. The value used when a grain value has no row in the source.
     * `undefined` (absent) is meaningfully different from `0`: absent leaves the column unset so
     * evaluation fails naming the missing reference, whereas `0` is a factual claim.
     * Wire type is `BigDecimal`, which Coral serialises as a JSON number.
     */
    defaultValue?: number;
    // CALC only.
    formula?: string;
    /**
     * The columns this formula reads — including those referenced inside aggregates (SUM/COUNT/…).
     * Advisory: the server re-derives dependencies from the formula text rather than trusting this
     * list, so it need not be exhaustive; the builder still sends the full read set. Mirrors the
     * contract's `dependsOn` (there is no separate aggregate-dependency field on the wire).
     */
    dependsOn?: string[];
}

/**
 * A Common Balance Pool definition: a grain, its columns, and its lifecycle state.
 * Timestamp fields (createdAt/lastUpdatedAt/submitDate/reviewDate) are epoch millis (the Coral
 * `Long`); the UI formats them for display (e.g. via DateUtils.formatDate).
 */
export interface CommonBalancePool {
    poolId: string;
    poolName?: string;
    description?: string;
    grain: CommonBalancePoolGrain;
    status?: CommonBalancePoolStatus;
    columns?: CommonBalancePoolColumn[];
    createdBy?: string;
    createdAt?: number;
    lastUpdatedBy?: string;
    lastUpdatedAt?: number;
    // Both submit + review actors are surfaced to evidence segregation of duties.
    submitUser?: string;
    submitDate?: number;
    reviewUser?: string;
    reviewDate?: number;
    comment?: string;
    /**
     * The highest definition version approved so far (MDA parity): N once approved (1, 2, …), and `-1`
     * for a pool with no approved version yet. Absent on a pool the backend has not versioned; the status
     * bar shows the version indicator only when it is present.
     */
    currentVersion?: number;
}

/** Response for `GetCommonBalancePools` (the CBP tab list + the MDA add-data-source picker). */
export interface ListCommonBalancePoolsResponse {
    pools: CommonBalancePool[];
}

/** One materialised row of a pool: a grain value and its column values, keyed by columnId. */
export interface CommonBalancePoolRow {
    grainValue: string;
    /**
     * The grain value's human description from FRAMES (the COA/segment master), e.g. the Cost Center
     * name for a cost-center-grained pool. Shown as the "<Grain> Description" column; joined by the
     * backend, absent until then.
     */
    grainDescription?: string;
    /** columnId → value. Wire type is `StringBigDecimalMap`; BigDecimal serialises as a JSON number. */
    values: Record<string, number>;
}

export interface GetCommonBalancePoolRowsResponse {
    rows: CommonBalancePoolRow[];
    /** Absent when this is the last page. */
    nextToken?: string;
    totalRowCount?: number;
}

export interface DownloadCommonBalancePoolResponse {
    /** Presigned S3 URL to the generated spreadsheet. */
    downloadUrl: string;
}

export type CommonBalancePoolValidationSeverity = 'BLOCKING' | 'WARNING';

export interface CommonBalancePoolValidationFinding {
    severity: CommonBalancePoolValidationSeverity;
    /** Stable check identifier, e.g. CYCLIC_FORMULA, POOL_TO_INPUT_MISMATCH. */
    code?: string;
    message?: string;
    columnIds?: string[];
    grainValues?: string[];
}

export interface CommonBalancePoolValidationResult {
    /** True when no BLOCKING finding is present. Warnings do not clear this flag. */
    passed?: boolean;
    findings?: CommonBalancePoolValidationFinding[];
}

export type CommonBalancePoolExecutionState =
    | 'PENDING'
    | 'RUNNING'
    | 'SUCCESS'
    | 'PARTIAL'
    | 'FAILED'
    | 'TIMED_OUT';

export interface CommonBalancePoolExecutionStatus {
    executionId?: string;
    poolId?: string;
    period?: string;
    state: CommonBalancePoolExecutionState;
    rowCount?: number;
    startedAt?: number;
    completedAt?: number;
    requestedBy?: string;
    errorMessage?: string;
    validationResult?: CommonBalancePoolValidationResult;
}

/** Preview evaluates a bounded sample; aggregates over a sample are indicative-only. */
export interface PreviewCommonBalancePoolResponse {
    rows: CommonBalancePoolRow[];
    validationResult?: CommonBalancePoolValidationResult;
    /** When true, mark aggregate (SUM/COUNT) columns indicative-only — they will not match a full run. */
    aggregatesAreSampled?: boolean;
    sampledRowCount?: number;
    totalRowCount?: number;
    /**
     * Full-table column totals (columnId → summed value), for the preview's "Total (N …)" footer row.
     * These are the full-run aggregates, NOT a sum of the sampled `rows` (which is only a bounded
     * preview), so the footer can show real totals alongside a few sample rows. Absent until the
     * backend computes them; the UI falls back to summing the loaded rows.
     */
    totals?: Record<string, number>;
}

export interface CreateCommonBalancePoolRequest {
    poolName: string;
    grain: CommonBalancePoolGrain;
    description?: string;
}

export interface UpdateCommonBalancePoolRequest {
    poolName?: string;
    description?: string;
    columns?: CommonBalancePoolColumn[];
    /** Set to PENDING_APPROVAL to submit, or ACTIVE/DRAFT_IN_PROGRESS by a reviewer to approve/reject. */
    status?: CommonBalancePoolStatus;
    comment?: string;
}

/**
 * Response for `UpdateCommonBalancePool`. `validationResult` is not decoration: a BLOCKING finding
 * means the update was refused, so a caller must branch on it rather than treating any 2xx as saved.
 */
export interface UpdateCommonBalancePoolResponse {
    pool: CommonBalancePool;
    validationResult?: CommonBalancePoolValidationResult;
}

/**
 * Response for `ExecuteCommonBalancePool` (202). Only the id + state are returned on trigger — the
 * rest of the run's detail is read by polling `GetCommonBalancePoolExecutionStatus`. Execute is
 * idempotent on (poolId, period): a call while a run is already in flight returns that run with its
 * current state, so a caller must not assume `PENDING`.
 */
export interface ExecuteCommonBalancePoolResponse {
    executionId: string;
    state: CommonBalancePoolExecutionState;
}
